// Trip detail (GET /:id) with selected-zone / destination / UTC times,
// travelers, links, holiday warnings and guarded soft delete.
import { useEffect, useState } from "react";
import { T } from "../../../app/tokens.js";
import { Modal } from "../../../components/Modal.jsx";
import { InternalLink } from "../../../components/InternalLink.jsx";
import { buildLocationUrl, buildMarketUrl } from "../../../services/buildUrl.js";
import { getTrip, deleteTrip } from "../../../data/projectTravelClient.js";
import { tx } from "./travelStyles.js";
import { fmtDateTime, fmtUtcRange, fmtCivil, holidayLabel, purposeLabel, purposeStyle, tripDateRange, zoneAbbr } from "./travelModel.js";

export function TripDetailModal({ tripId, zone, showNames, onClose, onEdit, onDeleted, onOpenProject }) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [retry, setRetry] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [del, setDel] = useState({ busy: false, error: null });

  useEffect(() => {
    if (!tripId) return undefined;
    let active = true;
    setState({ status: "loading", data: null, error: null });
    setConfirm(false); setDel({ busy: false, error: null });
    Promise.resolve().then(() => getTrip(tripId))
      .then((data) => { if (active) setState({ status: "ok", data, error: null }); })
      .catch((error) => { if (active) setState({ status: error?.status === 403 || error?.status === 401 ? "denied" : error?.name === "NotFoundError" ? "missing" : "error", data: null, error }); });
    return () => { active = false; };
  }, [tripId, retry]);

  const close = () => { if (!del.busy) onClose(); };
  const trip = state.data?.trip;
  const holidays = state.data?.holidays || [];
  const readOnly = trip && (!trip.canWrite || trip.archivedProject);

  const doDelete = () => {
    setDel({ busy: true, error: null });
    Promise.resolve().then(() => deleteTrip(trip.id))
      .then(() => { setDel({ busy: false, error: null }); onDeleted(trip.id); })
      .catch((err) => setDel({ busy: false, error: err?.message || "Could not delete this trip." }));
  };

  return (
    <Modal isOpen={Boolean(tripId)} onClose={close} maxWidth={520} title="Trip" ariaLabelledBy="travel-trip-title" testId="travel-trip-detail" closeTestId="travel-trip-close">
      <div style={{ padding: "14px 18px 18px", overflowY: "auto" }}>
        {state.status === "loading" && (
          <div data-testid="travel-trip-loading" aria-busy="true">
            {[60, 40, 90, 75, 80].map((w, i) => <div key={i} style={{ height: i ? 12 : 18, width: `${w}%`, background: T.lightGray, borderRadius: 5, marginBottom: 10 }} />)}
          </div>
        )}
        {state.status !== "loading" && state.status !== "ok" && (
          <div role={state.status === "error" ? "alert" : "status"} data-testid={`travel-trip-${state.status}`} style={{ fontSize: T.fsSm, color: T.dark }}>
            <div style={{ fontWeight: T.fwBold, marginBottom: 4 }}>{state.status === "missing" ? "This trip no longer exists." : state.status === "denied" ? "You don't have access to this trip." : "Couldn't load this trip."}</div>
            {state.error?.message && <div style={{ color: T.mid, marginBottom: 10 }}>{state.error.message}</div>}
            {state.status === "error" && <button type="button" style={tx.ghost} onClick={() => setRetry((n) => n + 1)} data-testid="travel-trip-retry">Retry</button>}
          </div>
        )}
        {trip && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
              <span style={{ ...tx.eyebrow, color: purposeStyle(trip.purposeType).fg, background: purposeStyle(trip.purposeType).bg, borderRadius: 4, padding: "1px 6px" }}>{purposeLabel(trip.purposeType)}</span>
              {trip.archivedProject && <span data-testid="travel-trip-archived" style={{ ...tx.eyebrow }}>History · archived project</span>}
            </div>
            <h3 id="travel-trip-heading" data-testid="travel-trip-name" style={{ margin: "4px 0 2px", fontSize: T.fsLg - 1, color: T.dark }}>{trip.title}</h3>
            <div style={{ color: T.mid, fontSize: T.fsSm }}>
              {trip.projectId
                ? <button type="button" data-testid="travel-trip-project" style={tx.link} onClick={() => onOpenProject(trip.projectId)}>{trip.projectName || "Project"}</button>
                : "Not linked to a project"}
              {trip.milestoneName && <> · milestone: <button type="button" data-testid="travel-trip-milestone"
                style={tx.link} onClick={() => onOpenProject(trip.projectId,"timeline")}>{trip.milestoneName}</button></>}
            </div>
            <dl style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "7px 10px", margin: "12px 0 0", fontSize: T.fsSm }}>
              <dt style={{ color: T.mid }}>Who</dt>
              <dd style={{ margin: 0 }} data-testid="travel-trip-who">
                {!trip.travelerCount ? <span style={{ color: T.mid }}>No one assigned yet</span>
                  : !showNames || !trip.travelers ? `${trip.travelerCount} traveler${trip.travelerCount === 1 ? "" : "s"}`
                  : trip.travelers.map((t) => (
                    <div key={t.personId}>{t.displayName}{t.org ? <span style={{ color: T.mid }}> · {t.org}</span> : null}
                      {t.active === false && <span style={{ ...tx.eyebrow, marginLeft: 6 }}>inactive</span>}
                      {(t.startsAtOverride || t.endsAtOverride) && <span style={{ color: T.mid }}> · {fmtDateTime(t.startsAtOverride || trip.startsAt, zone)} → {fmtDateTime(t.endsAtOverride || trip.endsAt, zone)}</span>}
                    </div>
                  ))}
              </dd>
              <dt style={{ color: T.mid }}>Where</dt>
              <dd style={{ margin: 0 }}>
                <span style={{ ...tx.flag, marginRight: 6 }}>{trip.destinationCountry}</span>
                {trip.destinationLocationId
                  ? <InternalLink href={buildLocationUrl(trip.destinationLocationId)} returnRecord={{ kind: "project", label: "Travel" }} data-testid="travel-trip-office" style={{ color: T.blue, textDecoration: "none" }}>{trip.destinationLabel}</InternalLink>
                  : trip.destinationLabel}
                {trip.destinationMarketId && <> · <InternalLink href={buildMarketUrl(trip.destinationMarketId)} returnRecord={{ kind: "project", label: "Travel" }} data-testid="travel-trip-market" style={{ color: T.blue, textDecoration: "none" }}>Market</InternalLink></>}
                <div style={{ color: T.mid, fontSize: T.fsXs }}>{trip.destinationTz}</div>
              </dd>
              {trip.allDay ? (
                <>
                  <dt style={{ color: T.mid }}>Dates</dt>
                  <dd style={{ margin: 0 }}>{tripDateRange(trip, zone)} <span style={{ color: T.mid }}>(destination dates, same in every zone)</span></dd>
                </>
              ) : (
                <>
                  <dt style={{ color: T.mid }}>Starts ({zoneAbbr(zone)})</dt><dd style={{ margin: 0 }} data-testid="travel-trip-start">{fmtDateTime(trip.startsAt, zone)}</dd>
                  <dt style={{ color: T.mid }}>Ends</dt><dd style={{ margin: 0 }}>{fmtDateTime(trip.endsAt, zone)}</dd>
                  <dt style={{ color: T.mid }}>At destination</dt><dd style={{ margin: 0 }}>{fmtDateTime(trip.startsAt, trip.destinationTz)} → {fmtDateTime(trip.endsAt, trip.destinationTz)}</dd>
                </>
              )}
              <dt style={{ color: T.mid }}>UTC</dt><dd style={{ margin: 0, fontVariantNumeric: "tabular-nums" }}>{fmtUtcRange(trip)}</dd>
              {trip.notes && <><dt style={{ color: T.mid }}>Notes</dt><dd style={{ margin: 0, whiteSpace: "pre-wrap" }}>{trip.notes}</dd></>}
            </dl>
            {!trip.travelerCount && trip.milestoneId && (
              <div style={{ marginTop: 10, background: T.travelAmberBg, border: `1px solid ${T.travelAmberBorder}`, borderRadius: 8, padding: "7px 9px", fontSize: T.fsSm, color: T.travelAmberFg }}>No one is booked for this milestone yet.</div>
            )}
            {holidays.length > 0 && (
              <div data-testid="travel-trip-holidays" style={{ marginTop: 10, background: T.travelAmberBg, border: `1px solid ${T.travelAmberBorder}`, borderRadius: 8, padding: "7px 9px", fontSize: T.fsSm, color: T.travelAmberFg }}>
                {holidays.map((h) => <div key={h.id}>{holidayLabel(h)}: {fmtCivil(h.date)}{h.endDate && h.endDate !== h.date ? ` – ${fmtCivil(h.endDate)}` : ""}{h.name ? ` · ${h.name}` : ""}</div>)}
              </div>
            )}
            {readOnly && <div data-testid="travel-trip-readonly" style={{ marginTop: 12, fontSize: T.fsXs, color: T.mid }}>{trip.archivedProject ? "Read-only history: this trip belongs to an archived project." : "Read-only: you can't edit trips for this project."}</div>}
            {!readOnly && !confirm && (
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button type="button" data-testid="travel-trip-edit" style={tx.ghost} onClick={() => onEdit(trip)}>Edit trip</button>
                <button type="button" data-testid="travel-trip-delete" style={tx.danger} onClick={() => setConfirm(true)}>Delete</button>
              </div>
            )}
            {!readOnly && confirm && (
              <div role="alertdialog" aria-label="Confirm delete" data-testid="travel-trip-delete-confirm" style={{ marginTop: 14, background: T.travelRedBg, border: `1px solid ${T.travelRedBorder}`, borderRadius: 8, padding: "9px 11px", fontSize: T.fsSm }}>
                <div style={{ marginBottom: 8 }}>Delete “{trip.title}”? It leaves the calendar and signals; the record is kept for audit.</div>
                {del.error && <div role="alert" style={{ ...tx.err, marginBottom: 6 }}>{del.error}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" style={tx.ghost} disabled={del.busy} onClick={() => setConfirm(false)} data-testid="travel-trip-delete-cancel">Keep trip</button>
                  <button type="button" style={{ ...tx.danger, opacity: del.busy ? 0.6 : 1 }} disabled={del.busy} onClick={doDelete} data-testid="travel-trip-delete-confirm-btn">{del.busy ? "Deleting…" : "Delete trip"}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
